/**
 * Which latency source a guest labels its samples with, given what its engine exposes.
 *
 * The seam between the guest-side emitter and the per-engine strategy: Android and desktop
 * Chromium expose an RTP timestamp on every rVFC frame, so the host correlates against its
 * send record (`rvfc-rtp`). WebKit omits `rtpTimestamp` from rVFC metadata, so Safari falls
 * through to the strongest key it does carry: `captureTime` or `receiveTime` on the frame
 * (`rvfc-capture-time`, correlated through the datachannel clock offset), else the
 * guest-clock-only input-to-photon tracker (`input-photon`) when it has produced a sample.
 * A null means "emit no labelled sample", never "fall back to something invented".
 */

import type { GuestBrowserCapability } from '../guestBrowserCapability';
import type { LatestRvfcObservation } from '../decodeHintCollector';
import type { LatencySampleSource } from './framePipelineContracts';

function finite(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value);
}

/**
 * @param inputPhotonAvailable true when the input-to-photon tracker has at least one sample
 *   this session. Only consulted on Safari; defaults to false so callers that cannot know
 *   never claim a source they do not have.
 */
export function selectLatencySource(
  capability: GuestBrowserCapability | undefined,
  observation: Pick<LatestRvfcObservation, 'rtpTimestamp' | 'captureTime' | 'receiveTime'>,
  inputPhotonAvailable = false,
): LatencySampleSource | null {
  const hasRtpTimestamp = finite(observation.rtpTimestamp);

  switch (capability?.engine) {
    case 'safari-ios': {
      // Frozen (PHASE_6.md): a frame clock beats the input tracker, because it labels every
      // frame rather than only the ones that followed a tagged input.
      const hasFrameClock = finite(observation.captureTime) || finite(observation.receiveTime);
      if (hasFrameClock) return 'rvfc-capture-time';
      return inputPhotonAvailable ? 'input-photon' : null;
    }
    case 'android-chrome':
    case 'desktop-chrome':
      return hasRtpTimestamp ? 'rvfc-rtp' : null;
    default:
      // No capability: trust the frame. An RTP timestamp is the strongest key there is.
      return hasRtpTimestamp ? 'rvfc-rtp' : null;
  }
}
